import { ref, onMounted, onUnmounted, watch } from 'vue';
import { supabaseClient } from '@/services/supabase';
import { useAuthStore } from '@/stores/auth';
import { redirectAfterSignOut } from '@/services/auth-service';
import { useRouter } from 'vue-router';

export interface IdleTimerOptions {
    timeoutMs?: number; // Default: 15 minutes
    warningMs?: number; // Default: 2 minutes before timeout
    refreshSessionMs?: number; // Default: 5 minutes before timeout
    enableSessionRefresh?: boolean; // Default: true
    enableWarning?: boolean; // Default: true
}

export function useIdleTimer(options: IdleTimerOptions = {}) {
    const {
        timeoutMs = 15 * 60 * 1000, // 15 minutes
        warningMs = 2 * 60 * 1000, // 2 minutes warning
        refreshSessionMs = 5 * 60 * 1000, // 5 minutes before timeout
        enableSessionRefresh = true,
        enableWarning = true,
    } = options;

    const { isSignedIn, currentOrganization } = useAuthStore();
    const router = useRouter();

    const lastActivity = ref(Date.now());
    const lastResetTime = ref(Date.now());
    const isIdle = ref(false);
    const isWarning = ref(false);
    const warningType = ref<'idle' | 'jwt'>('idle'); // Track whether warning is for idle or JWT expiry
    const warningDismissed = ref(false); // Track if user explicitly dismissed the warning
    const timeUntilTimeout = ref(0);

    // Cache JWT expiry to avoid calling getSession() repeatedly (which triggers auto-refresh)
    const cachedJwtExpiryMs = ref<number | null>(null);

    let idleCheckInterval: ReturnType<typeof setInterval>;
    let warningTimeout: ReturnType<typeof setTimeout>;
    let sessionRefreshTimeout: ReturnType<typeof setTimeout>;

    const resetTimer = async () => {
        const now = Date.now();
        lastActivity.value = now;
        lastResetTime.value = now;
        isIdle.value = false;

        // Only dismiss idle warnings on activity, not JWT warnings
        // JWT warnings require explicit user action (clicking "Extend Session")
        if (warningType.value === 'idle') {
            isWarning.value = false;
        }

        warningDismissed.value = false; // Reset dismissed flag when session is refreshed
        timeUntilTimeout.value = 0;

        // Clear existing timers
        if (warningTimeout) clearTimeout(warningTimeout);
        if (sessionRefreshTimeout) clearTimeout(sessionRefreshTimeout);

        // --- Local idle timeout always enforced ---
        if (enableWarning) {
            warningTimeout = setTimeout(() => {
                console.log('Local inactivity warning triggered');
                warningType.value = 'idle';
                isWarning.value = true;
                timeUntilTimeout.value = warningMs;
            }, timeoutMs - warningMs);
        }

        if (enableSessionRefresh) {
            sessionRefreshTimeout = setTimeout(() => {
                refreshSession();
            }, timeoutMs - refreshSessionMs);
        }

        // Skip JWT checks if auto-refresh is disabled (testing mode)
        // This prevents Supabase's getSession() from auto-refreshing the JWT
        if (!enableSessionRefresh) {
            console.log('🧪 Testing mode: Skipping JWT checks to prevent Supabase auto-refresh');
            return;
        }

        // --- Also check JWT expiry for refresh/warnings ---
        // Use cached expiry first, fall back to getSession if not cached
        let jwtRemaining = cachedJwtExpiryMs.value ? cachedJwtExpiryMs.value - Date.now() : null;

        if (jwtRemaining === null) {
            const {
                data: { session },
            } = await supabaseClient.auth.getSession();

            if (session?.expires_at) {
                cachedJwtExpiryMs.value = session.expires_at * 1000;
                jwtRemaining = cachedJwtExpiryMs.value - Date.now();
            }
        }

        if (jwtRemaining !== null && cachedJwtExpiryMs.value) {
            // Optional: if JWT expires *before* your local timeout,
            // use JWT timings for warnings/refresh
            if (jwtRemaining < timeoutMs) {
                if (enableWarning && jwtRemaining > warningMs) {
                    const warningDelay = jwtRemaining - warningMs;
                    console.log(`Setting JWT-based warning timeout in ${warningDelay / 1000}s`);
                    // Clear local timeout and use JWT-based timing
                    if (warningTimeout) clearTimeout(warningTimeout);
                    warningTimeout = setTimeout(() => {
                        console.log('JWT-based warning triggered');
                        warningType.value = 'jwt';
                        isWarning.value = true;
                        timeUntilTimeout.value = warningMs; // Set the warning duration
                    }, warningDelay);
                }

                if (enableSessionRefresh && jwtRemaining > refreshSessionMs) {
                    // Clear local timeout and use JWT-based timing
                    if (sessionRefreshTimeout) clearTimeout(sessionRefreshTimeout);
                    sessionRefreshTimeout = setTimeout(() => {
                        refreshSession();
                    }, jwtRemaining - refreshSessionMs);
                }
            }
        }
    };

    const refreshSession = async () => {
        try {
            const { data, error } = await supabaseClient.auth.refreshSession();
            if (error) {
                console.warn('Failed to refresh session:', error);
                // If refresh fails, we might need to log out
                handleIdle();
            } else {
                console.log('Session refreshed successfully');
                // Update cached JWT expiry after successful refresh
                if (data.session?.expires_at) {
                    cachedJwtExpiryMs.value = data.session.expires_at * 1000;
                    console.log(
                        `🔄 JWT expiry updated: ${Math.floor((cachedJwtExpiryMs.value - Date.now()) / 1000)}s remaining`,
                    );
                }
            }
        } catch (error) {
            console.error('Error refreshing session:', error);
            handleIdle();
        }
    };

    const handleIdle = async () => {
        console.log('User idle timeout reached, logging out');
        isIdle.value = true;

        const logoutRedirectUrl = currentOrganization.value?.metadata?.logoutRedirectUrl;

        try {
            await supabaseClient.auth.signOut();
            redirectAfterSignOut(logoutRedirectUrl, router);
        } catch (error) {
            console.error('Error during logout:', error);
            // Force redirect even if logout fails
            redirectAfterSignOut(logoutRedirectUrl, router);
        }
    };

    const checkIdle = async () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            // Another tab might be active; skip idle enforcement here
            return;
        }
        const now = Date.now();
        const timeSinceActivity = now - lastActivity.value;

        // Use cached JWT expiry to avoid calling getSession() (which triggers auto-refresh)
        let jwtRemaining: number | null = null;

        if (cachedJwtExpiryMs.value) {
            jwtRemaining = cachedJwtExpiryMs.value - now;
        } else {
            // If no cache, fetch once
            const {
                data: { session },
            } = await supabaseClient.auth.getSession();

            if (!session) {
                console.log('Session expired (no session), logging out');
                handleIdle();
                return;
            }

            if (session.expires_at) {
                cachedJwtExpiryMs.value = session.expires_at * 1000;
                jwtRemaining = cachedJwtExpiryMs.value - now;
            }
        }

        // Check JWT expiry time
        if (jwtRemaining !== null) {
            if (jwtRemaining <= 0) {
                console.log('JWT expired, logging out');
                handleIdle();
                return;
            }

            // Show warning if close to JWT expiry
            if (jwtRemaining <= warningMs && !isWarning.value && !warningDismissed.value) {
                console.log('JWT expiring soon, showing warning');
                warningType.value = 'jwt';
                isWarning.value = true;
                // Give user the full warning period, but cap it at actual time remaining
                timeUntilTimeout.value = Math.min(warningMs, jwtRemaining);
            } else if (isWarning.value) {
                // During warning, show actual time remaining but ensure it doesn't go below 0
                timeUntilTimeout.value = Math.max(0, jwtRemaining);
            }
        }

        // Fallback: enforce local idle timeout
        if (timeSinceActivity > timeoutMs) {
            console.log('Local activity timeout reached, logging out');
            handleIdle();
        }
    };

    const dismissWarning = () => {
        console.log('dismissWarning() called - isWarning before:', isWarning.value);
        isWarning.value = false;
        warningDismissed.value = true; // Prevent warning from re-triggering
        console.log('dismissWarning() - isWarning after:', isWarning.value, 'dismissed:', warningDismissed.value);
        // Don't reset timer - just dismiss the warning and let the user continue
        // The warning will only reappear if they become inactive again or session is refreshed
    };

    const extendSession = async () => {
        isWarning.value = false;
        warningDismissed.value = true; // Keep warning suppressed during refresh to prevent race condition
        console.log('extendSession() - Starting session refresh...');
        // Actually refresh the session to extend the JWT
        await refreshSession();
        // Then reset the timer with the new session (this will reset warningDismissed to false)
        await resetTimer();
        console.log('extendSession() - Session extended successfully');
    };

    // Activity event handlers - throttle to prevent excessive resets
    const handleActivity = () => {
        if (!isSignedIn.value) return;

        const now = Date.now();
        lastActivity.value = now;

        // Only reset timer if it's been more than 10 seconds since last reset
        // This prevents excessive timer resets from minor mouse movements
        if (now - lastResetTime.value > 10000) {
            resetTimer().catch(console.error);
        }
    };

    const handleVisibilityChange = () => {
        if (!isSignedIn.value) {
            return;
        }

        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
            console.log('Tab became visible, resetting idle timer');
            resetTimer().catch(console.error);
        } else {
            console.log('Tab hidden, pausing idle enforcement in this tab');
        }
    };

    const initializeTimer = () => {
        if (!isSignedIn.value) return;

        // Set up activity listeners
        const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll', 'wheel'];

        events.forEach((event) => {
            window.addEventListener(event, handleActivity, { passive: true });
        });

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleVisibilityChange);
        }

        // Start the idle check interval
        idleCheckInterval = setInterval(checkIdle, 1000);

        // Initialize timer
        resetTimer().catch(console.error);
    };

    const cleanupTimer = () => {
        // Clean up event listeners
        const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll', 'wheel'];

        events.forEach((event) => {
            window.removeEventListener(event, handleActivity);
        });

        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        }

        // Clear intervals and timeouts
        if (idleCheckInterval) clearInterval(idleCheckInterval);
        if (warningTimeout) clearTimeout(warningTimeout);
        if (sessionRefreshTimeout) clearTimeout(sessionRefreshTimeout);
    };

    // Watch for sign-in state changes
    watch(isSignedIn, (newValue, oldValue) => {
        if (newValue && !oldValue) {
            // User just signed in
            initializeTimer();
        } else if (!newValue && oldValue) {
            // User just signed out
            cleanupTimer();
            isIdle.value = false;
            isWarning.value = false;
            warningDismissed.value = false;
            timeUntilTimeout.value = 0;
            cachedJwtExpiryMs.value = null; // Clear cached JWT expiry
        }
    });

    onMounted(() => {
        initializeTimer();
    });

    onUnmounted(() => {
        cleanupTimer();
    });

    return {
        isIdle,
        isWarning,
        warningType,
        timeUntilTimeout,
        dismissWarning,
        extendSession,
        resetTimer,
    };
}
